import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Users, IndianRupee, TrendingDown, TrendingUp, Download, Plus, PieChart as PieIcon } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell } from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => "₹" + (n || 0).toLocaleString("en-IN");
const tip = { backgroundColor: "#131A26", border: "1px solid #243047", borderRadius: "8px", color: "#fff" };
const PIE_COLORS = ["#FACC15", "#60A5FA", "#22C55E", "#F59E0B", "#EF4444", "#8B5CF6", "#3B82F6"];

const Card = ({ label, value, icon: Icon, accent }) => (
  <div className="bg-card_bg border border-border_soft rounded-xl p-5">
    <div className="flex justify-between items-start mb-3">
      <h3 className="text-[11px] font-semibold text-text_secondary uppercase tracking-wider">{label}</h3>
      <div className={`p-2 rounded-lg ${accent}`}><Icon size={16} /></div>
    </div>
    <p className="text-2xl font-bold text-text_primary">{value}</p>
  </div>
);

export default function Reports() {
  const [summary, setSummary] = useState({});
  const [pnl, setPnl] = useState([]);
  const [leadAnalytics, setLeadAnalytics] = useState({ by_source: [], by_status: [], conversion_rate: 0 });
  const [expenses, setExpenses] = useState([]);
  const [expForm, setExpForm] = useState({ category: "Rent", amount: 0, note: "" });

  const fetchAll = useCallback(async () => {
    try {
      const [s, p, l, e] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/reports/summary`),
        axios.get(`${BACKEND_URL}/api/reports/pnl?months=6`),
        axios.get(`${BACKEND_URL}/api/leads/analytics`),
        axios.get(`${BACKEND_URL}/api/expenses`),
      ]);
      setSummary(s.data); setPnl(p.data); setLeadAnalytics(l.data); setExpenses(e.data);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const addExpense = async (e) => {
    e.preventDefault();
    if (!expForm.amount) return;
    await axios.post(`${BACKEND_URL}/api/expenses`, { ...expForm, amount: parseInt(expForm.amount) });
    setExpForm({ category: "Rent", amount: 0, note: "" });
    fetchAll();
  };

  return (
    <div data-testid="reports-page" className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex flex-col md:flex-row justify-between md:items-end gap-4">
        <div>
          <h1 className="text-4xl font-serif text-text_primary mb-2">Reports & Analytics</h1>
          <p className="text-text_secondary text-sm">Business performance, profit & loss, lead conversion and exports.</p>
        </div>
        <div className="flex gap-3">
          <button data-testid="export-members-btn" onClick={() => window.open(`${BACKEND_URL}/api/reports/export/members`, "_blank")} className="bg-card_bg border border-border_soft text-text_primary px-4 py-3 rounded-xl font-semibold hover:border-gold/50 transition-colors flex items-center gap-2 text-sm"><Download size={16} /> Members CSV</button>
          <button data-testid="export-invoices-btn" onClick={() => window.open(`${BACKEND_URL}/api/reports/export/invoices`, "_blank")} className="bg-card_bg border border-border_soft text-text_primary px-4 py-3 rounded-xl font-semibold hover:border-gold/50 transition-colors flex items-center gap-2 text-sm"><Download size={16} /> Invoices CSV</button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Card label="Total Members" value={summary.total_members || 0} icon={Users} accent="bg-gold/10 text-gold" />
        <Card label="New This Month" value={summary.new_members_month || 0} icon={Users} accent="bg-accent_success/10 text-accent_success" />
        <Card label="Total Revenue" value={fmt(summary.total_revenue)} icon={IndianRupee} accent="bg-gold/10 text-gold" />
        <Card label="Total Expenses" value={fmt(summary.total_expenses)} icon={TrendingDown} accent="bg-accent_danger/10 text-accent_danger" />
        <Card label="Net Profit" value={fmt(summary.net_profit)} icon={TrendingUp} accent="bg-olive/20 text-olive" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="bg-card_bg border border-border_soft p-6 rounded-xl lg:col-span-8">
          <h3 className="text-base font-semibold text-text_primary mb-6">Profit & Loss (6 months)</h3>
          <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={pnl} margin={{ top: 0, right: 0, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#243047" vertical={false} />
                <XAxis dataKey="name" stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `${v / 1000}k`} />
                <Tooltip contentStyle={tip} formatter={(v) => fmt(v)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="revenue" name="Revenue" fill="#FACC15" radius={[4, 4, 0, 0]} />
                <Bar dataKey="expenses" name="Expenses" fill="#EF4444" radius={[4, 4, 0, 0]} />
                <Bar dataKey="profit" name="Profit" fill="#60A5FA" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-card_bg border border-border_soft p-6 rounded-xl lg:col-span-4">
          <h3 className="text-base font-semibold text-text_primary mb-1 flex items-center gap-2"><PieIcon size={18} className="text-gold" /> Leads by Source</h3>
          <p className="text-xs text-text_secondary mb-4">Conversion rate: <span className="text-accent_success font-bold">{leadAnalytics.conversion_rate}%</span></p>
          <div className="h-56 w-full">
            {leadAnalytics.by_source?.length === 0 ? <div className="h-full flex items-center justify-center text-sm text-text_secondary">No leads yet</div> : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={leadAnalytics.by_source} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                    {leadAnalytics.by_source.map((e, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={tip} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {/* Expenses */}
      <div className="bg-card_bg border border-border_soft p-6 rounded-xl">
        <h3 className="text-base font-semibold text-text_primary mb-5">Expenses</h3>
        <form onSubmit={addExpense} className="flex flex-col md:flex-row gap-3 mb-6">
          <select data-testid="expense-category" value={expForm.category} onChange={(e) => setExpForm({ ...expForm, category: e.target.value })} className="bg-main_bg border border-border_soft rounded-xl p-3 text-text_primary text-sm md:w-48">
            {["Rent", "Salaries", "Equipment", "Utilities", "Marketing", "Maintenance", "Other"].map((c) => <option key={c}>{c}</option>)}
          </select>
          <input data-testid="expense-amount" type="number" min="0" placeholder="Amount (₹)" value={expForm.amount} onChange={(e) => setExpForm({ ...expForm, amount: e.target.value })} className="bg-main_bg border border-border_soft rounded-xl p-3 text-text_primary text-sm md:w-40" />
          <input data-testid="expense-note" type="text" placeholder="Note (optional)" value={expForm.note} onChange={(e) => setExpForm({ ...expForm, note: e.target.value })} className="flex-1 bg-main_bg border border-border_soft rounded-xl p-3 text-text_primary text-sm" />
          <button data-testid="add-expense-btn" type="submit" className="bg-primary text-[#111827] px-6 py-3 rounded-xl font-bold text-sm flex items-center gap-2 hover:bg-primary_hover transition-colors"><Plus size={16} /> Add</button>
        </form>
        <div className="space-y-2 max-h-72 overflow-y-auto custom-scrollbar">
          {expenses.length === 0 ? <p className="text-sm text-text_secondary/70 italic">No expenses recorded.</p> : expenses.map((x) => (
            <div key={x.id} className="flex justify-between items-center bg-main_bg border border-border_soft/60 rounded-xl px-4 py-3">
              <div><span className="text-sm font-semibold text-text_primary">{x.category}</span>{x.note && <span className="text-xs text-text_secondary ml-2">· {x.note}</span>}</div>
              <div className="flex items-center gap-4"><span className="text-xs text-text_secondary">{x.date}</span><span className="text-sm font-bold text-accent_danger">{fmt(x.amount)}</span></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
